import { useEffect, useRef, useState } from 'react';
import { detectBlueprintFile } from '@/services/dawPluginDetection/detectBlueprintFile';
import {
  blueprintDeviceKey,
  blueprintDeviceLabel,
  type BlueprintDetection,
  type BlueprintDevice,
} from '@/services/dawPluginDetection/blueprintDetectionTypes';

const requirementsPrefix = 'Detected devices: ';
export function withoutDetectedRequirements(requirements: string): string {
  return requirements.startsWith(requirementsPrefix)
    ? requirements.split('\n').slice(1).join('\n')
    : requirements;
}
export function withDetectedRequirements(
  requirements: string,
  devices: BlueprintDevice[]
): string {
  return [
    devices.length
      ? requirementsPrefix + devices.map(blueprintDeviceLabel).join(', ')
      : '',
    requirements,
  ]
    .filter(Boolean)
    .join('\n');
}

export function useBlueprintDetection(file?: File) {
  const abortRef = useRef<AbortController | undefined>(undefined);
  const [state, setState] = useState<{
    file?: File;
    status: 'idle' | 'checking' | 'done' | 'failed';
    result?: BlueprintDetection;
    selected: BlueprintDevice[];
    message?: string;
  }>({ status: 'idle', selected: [] });
  useEffect(() => {
    if (!file) return;
    const controller = new AbortController();
    abortRef.current = controller;
    void detectBlueprintFile(file, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted)
          setState({ file, status: 'done', result, selected: result.devices });
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setState({
            file,
            status: 'failed',
            selected: [],
            message:
              error instanceof Error
                ? error.message
                : 'Enter the requirements below.',
          });
      });
    return () => controller.abort();
  }, [file]);
  const current: typeof state =
    state.file === file
      ? state
      : { status: file ? 'checking' : 'idle', selected: [] };
  const toggle = (device: BlueprintDevice) =>
    setState((previous) => ({
      ...previous,
      selected: previous.selected.some(
        (d) => blueprintDeviceKey(d) === blueprintDeviceKey(device)
      )
        ? previous.selected.filter(
            (d) => blueprintDeviceKey(d) !== blueprintDeviceKey(device)
          )
        : [...previous.selected, device],
    }));
  return {
    ...current,
    toggle,
    cancel: () => {
      abortRef.current?.abort();
      setState((previous) =>
        file && (previous.file !== file || previous.status === 'checking')
          ? {
              file,
              selected: [],
              status: 'failed',
              message:
                'Automatic detection stopped. Your entered requirements will be used.',
            }
          : previous
      );
    },
  };
}
